import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Download, Share2, X } from "lucide-react";
import { formatDateFR } from "@/lib/constants";
import { b64ToSrc } from "@/lib/img";
import { useAuth } from "@/contexts/AuthContext";

const STORY_W = 1080;
const STORY_H = 1920;

/**
 * Build a 9:16 story-card PNG dataURL on the fly using <canvas>.
 * Loads the flyer (base64) and composites it with title / city / date overlays.
 */
async function buildStoryCard({ event, vipBy, organizerBy }) {
    const canvas = document.createElement("canvas");
    canvas.width = STORY_W; canvas.height = STORY_H;
    const ctx = canvas.getContext("2d");

    // Background gradient
    const grad = ctx.createLinearGradient(0, 0, 0, STORY_H);
    grad.addColorStop(0, "#0891b2");  // teal-600
    grad.addColorStop(1, "#10b981");  // emerald-500
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, STORY_W, STORY_H);

    // Flyer image (top zone) if any
    const flyerSrc = b64ToSrc(event.flyer_base64);
    if (flyerSrc) {
        const img = await loadImage(flyerSrc);
        // Center-crop to 1080 x ~1280
        const targetH = 1280;
        const ratio = img.width / img.height;
        let dw = STORY_W, dh = STORY_W / ratio;
        if (dh < targetH) { dh = targetH; dw = targetH * ratio; }
        const dx = (STORY_W - dw) / 2;
        const dy = 80;  // top margin
        // White rounded card
        roundRect(ctx, 40, dy - 20, STORY_W - 80, targetH + 40, 48, "#ffffff");
        ctx.save();
        roundClip(ctx, 60, dy, STORY_W - 120, targetH, 36);
        ctx.drawImage(img, dx, dy, dw, dh);
        ctx.restore();
    }

    // Brand pill top-left
    roundRect(ctx, 60, 60, 360, 90, 45, "rgba(255,255,255,0.95)");
    ctx.fillStyle = "#0891b2";
    ctx.font = "bold 44px Inter, system-ui, sans-serif";
    ctx.textBaseline = "middle";
    ctx.fillText("✨ Discoveur", 110, 105);

    // VIP stamp (top-right): a gold tag visible on the image when the
    // person sharing is a Discoveuriste VIP.
    if (vipBy) {
        const tagW = 540, tagH = 110, tagX = STORY_W - 60 - tagW, tagY = 60;
        const vipGrad = ctx.createLinearGradient(tagX, tagY, tagX + tagW, tagY);
        vipGrad.addColorStop(0, "#f59e0b");
        vipGrad.addColorStop(1, "#ec4899");
        roundRect(ctx, tagX, tagY, tagW, tagH, 55, vipGrad);
        ctx.fillStyle = "#ffffff";
        ctx.font = "900 42px Inter, system-ui, sans-serif";
        ctx.textBaseline = "middle";
        ctx.fillText("👑 Discoveuriste VIP", tagX + 30, tagY + tagH / 2);

        // Second pill below: who is sharing
        const subY = tagY + tagH + 16;
        roundRect(ctx, tagX, subY, tagW, 70, 35, "rgba(0,0,0,0.55)");
        ctx.fillStyle = "#ffffff";
        ctx.font = "600 30px Inter, system-ui, sans-serif";
        ctx.fillText(`${vipBy} vous partage cet évènement`, tagX + 30, subY + 35);
    } else if (organizerBy) {
        // Gold/Premium organisateur: invite to follow them
        const tagW = 600, tagH = 110, tagX = STORY_W - 60 - tagW, tagY = 60;
        const orgGrad = ctx.createLinearGradient(tagX, tagY, tagX + tagW, tagY);
        orgGrad.addColorStop(0, "#0891b2");
        orgGrad.addColorStop(1, "#10b981");
        roundRect(ctx, tagX, tagY, tagW, tagH, 55, orgGrad);
        ctx.fillStyle = "#ffffff";
        ctx.font = "900 42px Inter, system-ui, sans-serif";
        ctx.textBaseline = "middle";
        ctx.fillText("🎟️ Organisateur Discoveur", tagX + 30, tagY + tagH / 2);

        const subY = tagY + tagH + 16;
        roundRect(ctx, tagX, subY, tagW, 80, 40, "rgba(0,0,0,0.6)");
        ctx.fillStyle = "#ffffff";
        ctx.font = "600 28px Inter, system-ui, sans-serif";
        ctx.fillText(`${organizerBy}`, tagX + 30, subY + 28);
        ctx.font = "500 24px Inter, system-ui, sans-serif";
        ctx.fillStyle = "rgba(255,255,255,0.9)";
        ctx.fillText("Suivez-moi pour ne rien manquer", tagX + 30, subY + 58);
    }

    // Title block (bottom)
    const blockY = 1440;
    roundRect(ctx, 40, blockY, STORY_W - 80, 380, 48, "rgba(0,0,0,0.55)");
    ctx.fillStyle = "#ffffff";
    ctx.textBaseline = "top";
    ctx.font = "900 64px Inter, system-ui, sans-serif";
    wrapText(ctx, event.title || event.restaurant_name || "Un évènement sélectionné par Discoveur",
        80, blockY + 50, STORY_W - 160, 76);

    ctx.font = "600 36px Inter, system-ui, sans-serif";
    ctx.fillStyle = "rgba(255,255,255,0.92)";
    const meta = [
        formatDateFR(event.date) + (event.heure_debut ? ` · ${event.heure_debut}` : ""),
        `📍 ${event.city}${event.country ? ", " + event.country : ""}`,
    ].filter(Boolean);
    meta.forEach((line, i) => ctx.fillText(line, 80, blockY + 240 + i * 50));

    // CTA at the very bottom: invite to join Discoveur
    ctx.font = "700 30px Inter, system-ui, sans-serif";
    ctx.fillStyle = "rgba(255,255,255,0.85)";
    ctx.textBaseline = "bottom";
    ctx.textAlign = "center";
    ctx.fillText("discoveur.com — rejoins la communauté", STORY_W / 2, STORY_H - 60);
    ctx.textAlign = "left";

    return canvas.toDataURL("image/png", 0.92);
}

function loadImage(src) {
    return new Promise((resolve, reject) => {
        const im = new Image();
        im.crossOrigin = "anonymous";
        im.onload = () => resolve(im);
        im.onerror = reject;
        im.src = src;
    });
}

function roundRect(ctx, x, y, w, h, r, fill) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
    if (fill) { ctx.fillStyle = fill; ctx.fill(); }
}
function roundClip(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
    ctx.clip();
}
function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
    const words = String(text).split(" ");
    let line = "";
    for (let n = 0; n < words.length; n++) {
        const test = line + words[n] + " ";
        if (ctx.measureText(test).width > maxWidth && n > 0) {
            ctx.fillText(line.trim(), x, y);
            line = words[n] + " ";
            y += lineHeight;
        } else line = test;
    }
    ctx.fillText(line.trim(), x, y);
}

export default function StoryShareDialog({ event, open, onOpenChange }) {
    const { user } = useAuth();
    const nav = useNavigate();
    const [dataUrl, setDataUrl] = useState(null);
    const [building, setBuilding] = useState(false);
    const link = `${window.location.origin}/events/${event?.id}`;

    const isVip = user?.pro_plan === "vip" && user?.subscription_active;
    const isOrganizer = (user?.pro_plan === "gold" || user?.pro_plan === "premium")
        && user?.subscription_active;
    const profileIsPublic = !!user?.show_published_pseudo;
    const sharerName = profileIsPublic
        ? (user?.published_pseudo || user?.prenom || user?.pseudo || null)
        : null;
    // Build the headline label on the story image.
    const vipBy = isVip
        ? (sharerName ? `${sharerName}, Discoveuriste VIP` : "Un Discoveuriste VIP")
        : null;
    const organizerBy = isOrganizer
        ? (sharerName ? `${sharerName} — organisateur Discoveur` : "Un organisateur Discoveur")
        : null;

    useEffect(() => {
        if (!open || !event) { setDataUrl(null); return; }
        let cancelled = false;
        (async () => {
            setBuilding(true);
            try {
                const url = await buildStoryCard({ event, vipBy, organizerBy });
                if (!cancelled) setDataUrl(url);
            } catch (e) {
                if (process.env.NODE_ENV === "development") {
                    console.error("story build failed", e);
                }
            }
            if (!cancelled) setBuilding(false);
        })();
        return () => { cancelled = true; };
    }, [open, event, vipBy, organizerBy]);

    const download = () => {
        if (!dataUrl) return;
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = `discoveur-${(event?.title || event?.restaurant_name || "story").toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}.png`;
        document.body.appendChild(a); a.click(); a.remove();
    };

    const nativeShare = async () => {
        if (!dataUrl) return;
        try {
            const blob = await (await fetch(dataUrl)).blob();
            const file = new File([blob], "discoveur-story.png", { type: "image/png" });
            const baseText = organizerBy
                ? `${organizerBy} — suivez-moi pour ne rien manquer 🎟️\nDécouvrez l'événement et la communauté sur ${link}`
                : isVip
                    ? `Un Discoveuriste VIP vous partage cet évènement — rejoignez la communauté sur ${link}`
                    : `Un évènement sélectionné par Discoveur, rejoins la communauté — ${link}`;
            const sharePayload = {
                title: event?.title || event?.restaurant_name || "Discoveur",
                text: baseText,
                files: [file],
            };
            if (navigator.canShare && navigator.canShare(sharePayload)) {
                await navigator.share(sharePayload);
                onOpenChange?.(false);
                nav("/");
            } else if (navigator.share) {
                await navigator.share({ title: sharePayload.title, text: sharePayload.text, url: link });
                onOpenChange?.(false);
                nav("/");
            } else {
                download();
                onOpenChange?.(false);
                nav("/");
            }
        } catch (err) {
            if (err?.name !== "AbortError") download();
        }
    };

    if (!event) return null;
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-3xl max-w-md p-0 overflow-hidden" data-testid="story-share-dialog">
                <DialogHeader className="px-6 pt-6">
                    <DialogTitle className="flex items-center gap-2">
                        <Share2 size={18} />Partager en story
                    </DialogTitle>
                    <DialogDescription>
                        Story 9:16 prête à publier sur Instagram, WhatsApp Status, LinkedIn…
                    </DialogDescription>
                </DialogHeader>
                <div className="bg-muted px-6 pb-4 pt-3 flex items-center justify-center">
                    {building && <p className="text-sm text-muted-foreground py-12">Génération de la story…</p>}
                    {dataUrl && (
                        <img src={dataUrl} alt="story preview"
                            className="max-h-[60vh] w-auto rounded-2xl shadow-md"
                            data-testid="story-preview" />
                    )}
                </div>
                <div className="p-4 flex gap-2 flex-wrap bg-background border-t">
                    <Button onClick={nativeShare} disabled={!dataUrl}
                        className="flex-1 rounded-full bg-discover-gradient text-white"
                        data-testid="story-share-btn">
                        <Share2 size={16} className="mr-1" />Partager
                    </Button>
                    <Button onClick={download} disabled={!dataUrl}
                        variant="outline" className="rounded-full"
                        data-testid="story-download-btn">
                        <Download size={16} className="mr-1" />Télécharger
                    </Button>
                    <Button onClick={() => onOpenChange(false)}
                        variant="ghost" className="rounded-full" aria-label="Fermer">
                        <X size={16} />
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
